import { AnimatePresence, motion } from "motion/react";
import { useRef } from "react";

import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { useKey } from "@/hooks/useKey";
import { useProjectContext } from "@/providers/ProjectContext";
import { useTranslation } from "@/translations";

import { IconButton } from "../Button/IconButton";
import { ProjectList } from "./ProjectList";
import { useRecentProjects } from "./useRecentProjects";
import { projectSwitcherVariants } from "./variants";

const RECENT_PROJECT_COUNT = 3;
interface Props {
  isOpen: boolean;
  showCurrentProject?: boolean;
  showRecentProjects?: boolean;
  showPortfolio?: boolean;
  onClose: () => void;
  isDisabled?: boolean;
}
export function ProjectSwitcher({
  isOpen,
  showCurrentProject = true,
  showRecentProjects = true,
  showPortfolio,
  onClose,
  isDisabled,
}: Props): React.ReactNode {
  const rootRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { projectId } = useProjectContext();
  const { recentProjects, isLoading, projects } = useRecentProjects(
    projectId,
    showCurrentProject,
    RECENT_PROJECT_COUNT,
  );

  useKey("Escape", onClose, isOpen);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="relative flex max-h-screen-minus-24 w-full flex-col rounded-b-xl border border-t-0 border-border-secondary bg-background-default pb-0 text-text-secondary shadow-raised"
          ref={rootRef}
          variants={projectSwitcherVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <header className="flex shrink-0 items-center justify-between px-space-md pt-space-md">
            <span className="text-caption-emphasis leading-none uppercase">
              {t("component.project-switcher.title")}
            </span>
            <IconButton
              icon={<XCloseIcon />}
              title="Close project switcher"
              onClick={onClose}
              disabled={isDisabled}
              size="sm"
              withTooltip={false}
              isCircular
            />
          </header>
          {(showCurrentProject || projects.some((x) => x.id !== projectId)) && (
            <ProjectList
              projects={projects}
              recentProjects={recentProjects}
              currentProjectId={projectId}
              showCurrentProject={showCurrentProject}
              showRecentProjects={showRecentProjects}
              showPortfolio={showPortfolio}
              isLoading={isLoading}
              onClose={onClose}
              autoFocusSearchInput
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
